import CustomTable from "../../Components/CustomTable";
import { Edit, Trash2, UserMinus } from "lucide-react";
import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  deleteAdmin,
  getAdminList,
  getBookingList,
  updateAdminStatus,
} from "../../api/index";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../Components/Loader";
import moment from "moment";
import Switch from "react-switch";
import { ToastMsg } from "../../Components/ToastMsg";

const normalizeBookings = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => {
    return {
      index: index + 1,
      id: item?.id,
      name: item?.profile
        ? `${item.profile.first_name || ""} ${item.profile.last_name || ""}`
        : "N/A",
      email: item?.email || "N/A",
      phone: item?.profile
        ? `${item.profile.phone_country_code || ""} ${
            item.profile.phone_number || ""
          }`
        : "N/A",
      photo: item?.profile?.profile_pic || null,
      adminaccess: item?.is_active ?? false,
      gender: item?.profile?.gender, // map from is_active
    };
  });
};

const ManageUser = () => {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 10;
  const [activeTab, setActiveTab] = useState("1");
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [pageData, setPageData] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };
  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const handleDelete = async () => {
    const data = {
      id: deleteId,
    };
    if (deleteId) {
      try {
        setLoading(true);

        const response = await deleteAdmin(data);
        fetchAdmins();
        setLoading(false);
        setIsDeleteModalOpen(false);

        ToastMsg(response?.data?.message, "success");
      } catch (err) {
        setLoading(false);
        ToastMsg(err?.response?.data?.message, "error");
      }
    }
  };

  const abortRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const fetchAdmins = async ({ isSearch = false }) => {
    try {
      if (isSearch) {
        setLoading(false);
      } else {
        setLoading(true);
      }

      setError("");

      const items = await getAdminList(globalFilter);
      const normalizedData = normalizeBookings(items?.data?.data);
      setPageData(items?.data?.page_data);
      setAdmins(normalizedData || []);
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(e.message || "Failed to load users");
        setAdmins([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle admin access toggle
  const handleAdminAccessToggle = async (id, data, checked) => {
    handleStatusUpdate(id, data, checked);
  };
  const handleStatusUpdate = (id, data, checked) => {
    setLoading(true);
    let temp = JSON.parse(JSON.stringify(data));

    temp.adminaccess = checked;
    updateAdminStatus(temp)
      .then((res) => {
        setAdmins((prevBookings) =>
          prevBookings.map((booking) =>
            booking.id === id ? { ...booking, adminaccess: checked } : booking
          )
        );
        ToastMsg(res?.data?.message, "success");
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log("err", err);
        ToastMsg(err?.response?.data?.message);
      });
  };

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      fetchAdmins({ isSearch: true });
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [globalFilter]);

  useEffect(() => {
    fetchAdmins({ isSearch: false });
  }, [pageIndex, activeTab]);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const searchAdmin = () => {
    fetchAdmins({ search: globalFilter, isSearch: false });
  };

  const columns = useMemo(
    () => [
      {
        key: "index",
        label: "S.No.",
        minWidth: "36px",
        width: "36px",
        showSortIcon: false,
      },
      {
        key: "name",
        label: "NAME",
        minWidth: "150px",
        width: "150px",
        showSortIcon: false,
      },
      {
        key: "email",
        label: "EMAIL",
        minWidth: "180px",
        width: "180px",
        showSortIcon: false,
      },
      {
        key: "phone",
        label: "PHONE NUMBER",
        minWidth: "160px",
        width: "160px",
        showSortIcon: false,
      },
      {
        key: "photo",
        label: "PHOTO",
        minWidth: "80px",
        width: "80px",
        showSortIcon: false,
        render: (row) => (
          <>
            {row?.photo ? (
              <a
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#F6C244] font-medium underline"
              >
                View
              </a>
            ) : (
              "NA"
            )}
          </>
        ),
      },
      {
        key: "adminaccess",
        label: "ADMIN ACCESS",
        minWidth: "180px",
        width: "180px",
        showSortIcon: false,
        render: (value, row) => (
          <div className="text-center">
            <Switch
              onChange={(checked) =>
                handleAdminAccessToggle(row.id, row, checked)
              }
              checked={row.adminaccess}
              offColor="#888"
              onColor="#0ebc65"
              uncheckedIcon={false}
              checkedIcon={false}
              height={20}
              width={40}
              handleDiameter={16}
            />
          </div>
        ),
      },
      {
        key: "action",
        label: "ACTION",
        minWidth: "180px",
        width: "180px",
        showSortIcon: false,
        render: (value, row) => (
          <div className="flex gap-[48px] items-center ">
            <button
              onClick={() => {
                navigate("/manage-user/edit", {
                  mode:true,
                  state: row,
                });
              }}
              className="text-white hover:text-[#F6C244] transition-colors"
            >
              <Edit size={20} />
            </button>
            <button
              onClick={() => {
                openDeleteModal(row.id);
              }}
              className="text-white hover:text-[#F6C244] transition-colors"
            >
              <Trash2 size={20} />
            </button>
          </div>
        ),
      },
    ],
    [pageIndex, pageSize]
  );

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <div className="flex flex-col gap-4 p-8 bg-[#161616] min-h-screen">
        <div className="rounded-[8px] w-full">
          <CustomTable
            data={admins}
            columns={columns}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            pageSize={pageSize}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            fetchUsers={searchAdmin}
            disableClientFilter={true}
            pageData={pageData}
          />
        </div>
      </div>
      {/* Delete Vehicle Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 z-50">
          <div className="bg-[#1A1A1A] rounded-xl shadow-lg w-[400px] p-6 text-center border border-[#F9AC1D] bg-gradient-to-b from-[#2b220fdb] to-[#18140bec]">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F3B7531A]">
                <UserMinus className="text-[#F3B753] w-6 h-6" />
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-2">Delete User?</h2>
            <p className="text-sm text-gray-300 mb-6">
              Are you sure you want to delete user?
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={closeDeleteModal}
                className="flex-1 border border-[#F3B753] text-[#F3B753] py-3 rounded-lg font-medium text-sm hover:bg-[#F3B753] hover:text-black transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 bg-[#F3B753] text-black py-2 rounded-lg font-medium text-sm hover:bg-[#e6a800] transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageUser;
